<template>
  <v-modal
    v-if="opened"
    :title="_getTitle()"
    :actions="getActions"
    classType="primary"
    version="new"
    extraBackdropClass="new-template-modal__wrapper"
  >
    <VSwitcher
      v-if="!campaignToEdit"
      :activeKey="dataKey"
      :switcherKeys="switcherKeys"
      type="element"
      @change="_updateLayout"
      extraClass="new-template-modal"
    ></VSwitcher>

    <WithLoader
      :isLoading="_loading"
      classType="primary"
      extraClass="create-new-campaign-modal"
    >
      <div class="templates-new-form__wrapper">
        <CampaignsCreateForm
          :initialCampaignValues="initialCampaignValues"
          :campaignToEdit="campaignToEdit"
          :templateType="dataKey"
          @submit="_processSubmitCampaign"
        ></CampaignsCreateForm>
      </div>
    </WithLoader>
  </v-modal>
</template>

<script>
import Vue from "vue";
import VueTypes from "vue-types";
import { mapActions, mapGetters } from "vuex";
import cloneDeep from "lodash/cloneDeep";
import map from "lodash/map";

// components
import VModal from "@/components/VModal";
import TemplatesCreateForm from "@/components/TemplatesCreateForm";
import VSwitcher from "@/components/VSwitcher";
import CampaignsCreateForm from "@/components/CampaignsCreateForm";

// mixins
import withForm from "@/mixins/withForm";
import withToasted from "@/mixins/withToasted";
import withNavigate from "@/mixins/withNavigate";

import postcardIcon from "@/assets/images/postcard-icon-light.svg";
import postcardIconActive from "@/assets/images/postcard-icon-active.svg";
import letterIcon from "@/assets/images/letter-icon.svg";
import letterIconActive from "@/assets/images/letter-icon-active.svg";

import { convertCampaignFormDataToApiFormat } from "@/utils/util";
import { accountAddressFormFields } from "@/constants/forms";
import isEqual from "lodash/isEqual";

export default Vue.component("CampaignsCreateNewModal", {
  props: {
    opened: VueTypes.bool.isRequired,
    campaignId: VueTypes.string
  },

  mixins: [withForm, withToasted, withNavigate],

  data() {
    return {
      name: "newCampaign",
      addressFormName: "createCampaignAddress",
      dataKey: "postcard",
      postcardType: "postcard-4x6",
      campaignToEdit: null,
      initialCampaignValues: null,
      currentTags: []
    };
  },

  components: {
    VModal,
    TemplatesCreateForm,
    VSwitcher,
    CampaignsCreateForm
  },

  methods: {
    _processSubmitCampaign() {
      return this.campaignToEdit
        ? this._editCampaign()
        : this._createCampaign();
    },

    _getTitle() {
      return this.campaignToEdit ? "Edit Campaign" : "New Campaign";
    },

    _onCancel() {
      if (this.initialCampaignValues) {
        const initialCopy = cloneDeep(this.initialCampaignValues);

        // common fields
        this.updateValues({
          formName: this.name,
          fields: initialCopy.mainInfo
        });

        // address fields
        this.updateValues({
          formName: this.addressFormName,
          fields: initialCopy.fromAddress
        });
      } else {
        this.clearForm();
        this.clearForm(this.addressFormName);
      }

      //refetch tags after closing modal window
      this.fetchFormTagsList();
      this.$emit("close");
    },

    _performSendRequest() {
      this.validateForm();
      const { useAccount } = this.formValues;

      if (useAccount === "no") {
        this.validateForm(this.addressFormName);
      }

      if (this.isValid) {
        let values = { ...this.formValues };

        if (useAccount === "no") {
          // concat two forms to submit
          if (this.addressFormValid) {
            values = { ...values, ...this.addressFormValues };
          } else {
            this.toast("Can't create campaign, please check fields.", "error");
            return;
          }
        }

        return convertCampaignFormDataToApiFormat(
          values,
          this.getAccount,
          useAccount === "yes"
        );
      }
    },

    _createCampaign() {
      let values = this._performSendRequest();

      if (values) {
        values = {
          ...values,
          type: this.dataKey
        };

        this.createCampaign({
          data: values,
          onSuccess: this._handleSuccessSave,
          onError: e => this._handleErrorCase(e)
        });
      }
    },

    _editCampaign() {
      let values = this._performSendRequest();
      if (values) {
        values = {
          ...values,
          type: this.dataKey
        };
        this.editCampaign({
          id: this.campaignToEdit,
          data: values,
          onSuccess: resp => {
            if (!isEqual(values.tags, this.currentTags)) {
              this.fetchPreview(this.campaignToEdit, {
                onSuccess: () => {},
                onError: e => this.toast(e, "error")
              });
            }
            this.toast(`Campaign ${resp.name} has been edited!`);
            this.$emit("campaigncreated");
            this.clearForm();
            this.clearForm(this.addressFormName);
            this.campaignToEdit = null;
          },
          onError: e => {
            const message =
              typeof e === "string"
                ? e
                : "Can't create campaign, please try again later.";

            this.toast(message, "error");
          }
        });
      }
    },

    _updateLayout(active) {
      this.dataKey = active;
      this.clearField("template");
    },

    _handleSuccessSave(resp) {
      this.toast(`Campaign ${resp.name} has been created!`);
      this.$emit("campaigncreated");
      this.clearForm();
      this.clearForm(this.addressFormName);
      this.navigate(`/campaigns/${resp.id}/?o=${resp.type}`);
    },

    _handleErrorCase(e) {
      this.showServerErrors(e);
      this.syncErrorsFromServer(e);
    },

    ...mapActions("templates", ["createTemplate", "fetchTemplatesList"]),
    ...mapActions("lettersTemplates", [
      "createLetterTemplate",
      "fetchLettersTemplatesList"
    ]),
    ...mapActions("contacts", ["fetchTagsList"]),
    ...mapActions("campaigns", [
      "createCampaign",
      "fetchCampaign",
      "editCampaign",
      "fetchPreview"
    ]),
    ...mapActions("tags", ["fetchFormTagsList"])
  },

  computed: {
    addressFields() {
      return map(accountAddressFormFields, item => {
        if (item.name === "country") {
          item.extraClassRoot = "campaigns-create";
        }

        item.classType = "primary";
        return item;
      });
    },

    _loading() {
      return this.isTemplatesLoading || this.isCampaignsLoading;
    },

    addressFormValues() {
      return this.$store.getters["forms/getValuePairs"](this.addressFormName);
    },

    shouldCreateBtnDisable() {
      if (this.formValues && this.formValues.useAccount === "no") {
        return (
          (!this.isFormValid(this.addressFormName) &&
            !this.isValid &&
            this.isFormTouched()) ||
          this._loading
        );
      }
      return (!this.isValid && this.isFormTouched()) || this._loading;
    },

    getActions() {
      return [
        {
          label: "Cancel",
          value: "cancel",
          type: "primary",
          size: "wide",
          noBorder: true,
          inverted: true,
          click: () => {
            this._onCancel();
            this.$emit("close");
          }
        },
        {
          label: this.campaignToEdit ? "Save" : "Create",
          value: "add",
          type: "primary",
          size: "regular",
          click: this._processSubmitCampaign,
          disabled: this.shouldCreateBtnDisable
        }
      ];
    },

    switcherKeys() {
      return [
        {
          key: "postcard",
          label: "Postcard",
          icon: postcardIcon,
          iconActive: postcardIconActive
        },

        {
          key: "letter",
          label: "Letter",
          icon: letterIcon,
          iconActive: letterIconActive
        }
      ];
    },

    addressFormValid() {
      return this.$store.getters["forms/isValid"](this.addressFormName);
    },

    ...mapGetters("templates", {
      isTemplatesLoading: "isLoading"
    }),
    ...mapGetters("campaigns", {
      isCampaignsLoading: "isLoading"
    }),
    ...mapGetters("account", ["getAccount"])
  },

  created() {
    this.fetchTemplatesList({ page_size: 100 });
    this.fetchLettersTemplatesList({ page_size: 100 });
    this.fetchCampaign({
      id: this.campaignId,
      onSuccess: campaign => {
        this.currentTags = [...campaign.tags];
      }
    });
    this.fetchFormTagsList();
  },

  beforeUpdate() {
    if (this.campaignId !== this.campaignToEdit) {
      // If campaignId has been changed (by clicking on 'edit' button)

      // If 'new' clicked after edit need to clear form from old values
      if (this.campaignId === null && this.campaignToEdit) {
        this.clearForm();

        // clear address form
        this.clearForm(this.addressFormName);
      }

      this.campaignToEdit = this.campaignId;

      // when clicked 'new campaign' don't need to fetch anything here
      if (!this.campaignToEdit) {
        return;
      }

      this.fetchCampaign({
        id: this.campaignToEdit,
        onSuccess: campaign => {
          const { fromAddress, ...rest } = cloneDeep(campaign);
          const { template_id, type } = rest;

          // switcher position
          this.dataKey = type;

          // initial selected template
          rest.template = template_id;

          // common fields
          this.initializeForm({
            formName: this.name,
            fields: rest
          });

          // address fields
          this.initializeForm({
            formName: this.addressFormName,
            fields: fromAddress
          });

          this.initialCampaignValues = {
            fromAddress: cloneDeep(fromAddress),
            mainInfo: cloneDeep(rest)
          };
        }
      });
    }
  }
});
</script>

<style lang="scss" scoped>
.new-template-modal {
  margin-bottom: 30px;

  &__wrapper /deep/ .v-modal__container {
    min-height: 90%;

    @media screen and (min-height: 900px) {
      min-height: 746px;
    }
  }
}

.create-new-campaign-modal {
  @include customScrollbar();
  padding-right: 15px;

  overflow-y: auto;
  height: 90%;
}

.new-template-modal__wrapper /deep/ .v-modal__body {
  height: 500px;
}
</style>

<style lang="scss">
.template-wrapper {
  /*margin: 20px 0 0 0;*/
}

.campaigns-create-tags,
.campaigns-create__address-wrapper {
  margin-bottom: 20px;
}
</style>
